// +++ Cordchat - recovered from the 2024 production bundle (patched mattermost-redux
// selectors/entities/channels + utils/channel_utils). These selectors work across ALL teams
// of the current user, unlike the upstream ones that are scoped to the current team.

import {createSelector} from 'reselect';

import {getAllChannels, getCurrentChannelId, getMyChannelMemberships} from 'mattermost-redux/selectors/entities/channels';
import {getMyPreferences, getTeammateNameDisplaySetting} from 'mattermost-redux/selectors/entities/preferences';
import {getCurrentUser} from 'mattermost-redux/selectors/entities/users';
import {General, Preferences} from 'mattermost-redux/constants';
import {
    completeDirectChannelInfo,
    getDirectChannelName,
    getUserIdFromChannelName,
    isDirectChannel,
    isGroupChannel,
    sortChannelsByDisplayName
} from 'mattermost-redux/utils/channel_utils';
import {getPreferencesByCategory} from 'mattermost-redux/utils/preference_utils';

// All channels the store knows about, in every team, sorted by display name.
export const getChannelsInAllTeam = createSelector(
    getAllChannels,
    getCurrentUser,
    (allChannels, currentUser) => {
        let locale = 'en';
        if (currentUser && currentUser.locale) {
            locale = currentUser.locale;
        }
        return Object.values(allChannels).sort(sortChannelsByDisplayName.bind(null, locale));
    }
);

// Channels in every team that the current user is a member of.
export const getMyAllChannels = createSelector(
    getChannelsInAllTeam,
    getMyChannelMemberships,
    (channels, myMembers) => {
        return [...channels].filter((c) => myMembers.hasOwnProperty(c.id));
    }
);

// Same shape as the upstream getChannelsByCategory, but across all teams and with every
// direct/group channel listed regardless of the show/hide preferences.
export const getMyAllChannelsByCategory = createSelector(
    getCurrentChannelId,
    getMyAllChannels,
    getMyPreferences,
    getTeammateNameDisplaySetting,
    (state) => state.entities.users,
    (currentChannelId, channels, myPreferences, teammateNameDisplay, usersState) => {
        const allChannels = channels.map((c) => {
            const channel = {...c};
            channel.isCurrent = c.id === currentChannelId;
            return channel;
        });

        return buildNewDisplayableChannelList(usersState, allChannels, myPreferences, teammateNameDisplay);
    }
);

// ---- helpers (mirrors of the non-exported upstream channel_utils helpers) ----

function getUserLocale(userId, profiles) {
    let locale = 'en';
    if (profiles && profiles[userId] && profiles[userId].locale) {
        locale = profiles[userId].locale;
    }
    return locale;
}

function isNotDeletedChannel(channel) {
    return channel.delete_at === 0;
}

function isOpenChannel(channel) {
    return channel.type === General.OPEN_CHANNEL;
}

function isPrivateChannel(channel) {
    return channel.type === General.PRIVATE_CHANNEL;
}

function isFavoriteChannel(myPreferences, channel) {
    const fav = myPreferences[`${Preferences.CATEGORY_FAVORITE_CHANNEL}--${channel.id}`];
    return fav && fav.value === 'true';
}

function isDirectChannelForUser(userId, otherUserId, channel) {
    return channel.type === General.DM_CHANNEL && getUserIdFromChannelName(userId, channel.name) === otherUserId;
}

function createFakeChannel(userId, otherUserId) {
    return {
        name: getDirectChannelName(userId, otherUserId),
        last_post_at: 0,
        total_msg_count: 0,
        type: General.DM_CHANNEL,
        fake: true
    };
}

// Direct channels the user asked to show in the sidebar but which do not exist in the store yet.
function createMissingDirectChannels(currentUserId, allChannels, myPreferences) {
    const directChannelsDisplayPreferences = getPreferencesByCategory(myPreferences, Preferences.CATEGORY_DIRECT_CHANNEL_SHOW);

    return Array.from(directChannelsDisplayPreferences).
        filter((entry) => entry[1] === 'true').
        map((entry) => entry[0]).
        filter((teammateId) => !allChannels.some(isDirectChannelForUser.bind(null, currentUserId, teammateId))).
        map((teammateId) => createFakeChannel(currentUserId, teammateId));
}

function sortFavorites(locale, a, b) {
    const aDisplay = a.display_name.toLowerCase();
    const bDisplay = b.display_name.toLowerCase();
    return aDisplay.localeCompare(bDisplay, locale, {numeric: true});
}

export function buildNewDisplayableChannelList(usersState, allChannels, myPreferences, teammateNameDisplay) {
    const {currentUserId, profiles} = usersState;
    const missingDirectChannels = createMissingDirectChannels(currentUserId, allChannels, myPreferences);
    const locale = getUserLocale(currentUserId, profiles);

    const channels = allChannels.concat(missingDirectChannels).
        map(completeDirectChannelInfo.bind(null, usersState, teammateNameDisplay)).
        filter(isNotDeletedChannel).
        sort(sortChannelsByDisplayName.bind(null, locale));

    const favoriteChannels = channels.filter(isFavoriteChannel.bind(null, myPreferences)).sort(sortFavorites.bind(null, locale));
    const notFavoriteChannels = channels.filter((c) => !isFavoriteChannel(myPreferences, c));
    const directAndGroupChannels = notFavoriteChannels.filter((c) => isGroupChannel(c) || isDirectChannel(c));

    return {
        favoriteChannels,
        publicChannels: notFavoriteChannels.filter(isOpenChannel),
        privateChannels: notFavoriteChannels.filter(isPrivateChannel),
        directAndGroupChannels
    };
}

// ---



// WEBPACK FOOTER //
// ./custom-redux/selectors/channels.jsx